import { useMemo } from "react";
import type { Payback } from "../api";
import { channelColor, channelLabel, money } from "../format";

/**
 * The payback curve. X = days since acquisition, Y = cumulative revenue per
 * customer, one line per channel, a dashed CAC line per channel, and the
 * crossing point (payback day) marked.
 *
 * Cohort maturity is respected: a cohort month only contributes at day d once
 * every member of it has had d days to convert. That's why lines stop short of
 * day 90 — the alternative is dragging the average down with cohorts that
 * haven't had time yet, which would understate payback.
 */

const MAX_DAY = 90;
const X0 = 58;
const X1 = 846;
const Y0 = 18;
const Y1 = 356;

export type Series = {
  channel: string;
  name: string;
  color: string;
  path: string;
  dashed: boolean;
  cacCents: number | null;
  ltvCents: number;
  paybackDay: number | null;
  customers: number;
  lastDay: number;
};

export function PaybackChart({
  data,
  currency,
  selected,
}: {
  data: Payback;
  currency: string;
  selected: string;
}) {
  const model = useMemo(() => buildPayback(data, selected), [data, selected]);

  if (!model || model.series.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "44px 0 40px",
          textAlign: "center",
          color: "var(--text-4)",
        }}
      >
        <div style={{ fontSize: 14.5, fontWeight: 500, color: "var(--text-2)" }}>
          Keeper is gathering data.
        </div>
        <div style={{ fontSize: 12.5 }}>
          The curve needs identities with a first-touch channel and at least one transaction.
        </div>
      </div>
    );
  }

  const { series, yMax } = model;
  const X = (d: number) => X0 + (d / MAX_DAY) * (X1 - X0);
  const Y = (cents: number) => Y1 - (cents / yMax) * (Y1 - Y0);
  const yTicks = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  return (
    <div>
      <svg
        viewBox="0 0 980 404"
        style={{ width: "100%", height: "auto", overflow: "visible", display: "block" }}
        role="img"
        aria-label="Cumulative revenue per customer by days since acquisition, per channel, against CAC."
      >
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={X0} y1={Y(v)} x2={X1} y2={Y(v)} stroke="var(--border-hairline)" strokeWidth={1} />
            <text x={46} y={Y(v) + 6} fill="var(--text-faint)" fontSize={17} textAnchor="end">
              {money(v, currency)}
            </text>
          </g>
        ))}

        {[0, 15, 30, 45, 60, 75, 90].map((d) => (
          <text key={`x${d}`} x={X(d)} y={384} fill="var(--text-faint)" fontSize={17} textAnchor="middle">
            {d}
          </text>
        ))}

        {/* CAC lines sit under the curves so a crossing is never obscured. */}
        {series.map(
          (s) =>
            s.cacCents !== null && (
              <g key={`cac${s.channel}`}>
                <line
                  x1={X0}
                  y1={Y(s.cacCents)}
                  x2={X1}
                  y2={Y(s.cacCents)}
                  stroke={s.color}
                  strokeWidth={1}
                  strokeDasharray="6 6"
                  opacity={0.45}
                />
                <text
                  x={X1 + 8}
                  y={Y(s.cacCents) + 5.5}
                  fill={s.color}
                  fontSize={16}
                  opacity={0.9}
                >
                  {`CAC ${money(s.cacCents, currency)}`}
                </text>
              </g>
            )
        )}

        {series.map((s) => (
          <path
            key={`p${s.channel}`}
            d={s.path}
            fill="none"
            stroke={s.color}
            strokeWidth={s.dashed ? 1.6 : 2}
            strokeDasharray={s.dashed ? "3 5" : undefined}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {series.map(
          (s) =>
            s.paybackDay !== null &&
            s.cacCents !== null && (
              <g key={`m${s.channel}`}>
                <circle
                  cx={X(s.paybackDay)}
                  cy={Y(s.cacCents)}
                  r={6}
                  fill="var(--surface)"
                  stroke={s.color}
                  strokeWidth={2.4}
                />
                <text
                  x={X(s.paybackDay)}
                  y={Y(s.cacCents) - 16}
                  fill={s.color}
                  fontSize={17}
                  textAnchor="middle"
                >
                  {`day ${s.paybackDay}`}
                </text>
              </g>
            )
        )}

        <line x1={X0} y1={Y1} x2={X1} y2={Y1} stroke="var(--border-strong)" strokeWidth={1} />
        <text x={(X0 + X1) / 2} y={400} fill="var(--text-faint)" fontSize={16} textAnchor="middle">
          Days since acquisition
        </text>
      </svg>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "14px 34px",
          marginTop: 18,
          paddingTop: 18,
          borderTop: "1px solid var(--border-hairline)",
        }}
      >
        {series.map((s) => (
          <div key={`l${s.channel}`} style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 168 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 14, height: 2, background: s.color, flex: "none" }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
            </div>
            <div className="num" style={{ fontSize: 11.5, color: "var(--text-faint)", paddingLeft: 22 }}>
              {s.cacCents !== null
                ? `CAC ${money(s.cacCents, currency)} · LTV ${money(s.ltvCents, currency)}`
                : `no channel cost · LTV ${money(s.ltvCents, currency)}`}
            </div>
            <div style={{ fontSize: 11.5, color: s.color, paddingLeft: 22 }}>{verdict(s)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function verdict(s: Series): string {
  if (s.channel === "unattributed") return "shown, never smeared";
  if (s.cacCents === null) return "no channel cost to pay back";
  if (s.paybackDay === null) {
    return s.lastDay < MAX_DAY
      ? `no payback by day ${s.lastDay} (cohorts still maturing)`
      : "no payback in 90d";
  }
  return s.paybackDay <= 45 ? `paid back day ${s.paybackDay}` : `payback day ${s.paybackDay} · at risk`;
}

export function buildPayback(data: Payback, selected: string): { series: Series[]; yMax: number } | null {
  const cacBy = new Map(data.cac.map((c) => [c.channel, c]));

  // channel -> cohort_month -> (day_n -> cumulative cents)
  const byChannel = new Map<string, Map<string, { size: number; days: Map<number, number> }>>();
  for (const row of data.cohorts) {
    let months = byChannel.get(row.channel);
    if (!months) byChannel.set(row.channel, (months = new Map()));
    let m = months.get(row.cohort_month);
    if (!m) months.set(row.cohort_month, (m = { size: row.identities, days: new Map() }));
    m.days.set(row.day_n, Number(row.cum_revenue_cents));
  }

  const series: Series[] = [];
  let yMax = 0;

  for (const [channel, months] of byChannel) {
    // Unattributed is always shown, even when a single channel is selected —
    // it is a category, not a channel, and hiding it would misrepresent revenue.
    if (selected !== "All" && channel !== selected && channel !== "unattributed") continue;

    const mature = [...months.entries()].map(([month, m]) => ({
      maturity: maturityDays(month),
      size: m.size,
      days: m.days,
    }));

    const points: Array<{ d: number; cents: number }> = [];
    for (let d = 0; d <= MAX_DAY; d++) {
      let num = 0;
      let den = 0;
      for (const c of mature) {
        if (c.maturity < d) continue;
        den += c.size;
        num += carried(c.days, d);
      }
      if (den === 0) break;
      points.push({ d, cents: num / den });
    }
    if (points.length < 2) continue;

    const cacRow = cacBy.get(channel);
    const cacCents = cacRow?.cac_cents != null ? Number(cacRow.cac_cents) : null;
    const ltvCents = points[points.length - 1].cents;

    let paybackDay: number | null = null;
    if (cacCents !== null) {
      const hit = points.find((p) => p.cents >= cacCents);
      paybackDay = hit ? hit.d : null;
    }

    yMax = Math.max(yMax, ltvCents, cacCents ?? 0);
    series.push({
      channel,
      name: channelLabel(channel),
      color: channelColor(channel),
      dashed: channel === "unattributed",
      path: "",
      cacCents,
      ltvCents,
      paybackDay,
      customers: cacRow?.customers ?? 0,
      lastDay: points[points.length - 1].d,
    });
    // Path is written after yMax is known, so stash the points on the series.
    (series[series.length - 1] as Series & { _pts: typeof points })._pts = points;
  }

  if (series.length === 0) return null;

  yMax = niceCeiling(yMax);
  const X = (d: number) => X0 + (d / MAX_DAY) * (X1 - X0);
  const Y = (cents: number) => Y1 - (cents / yMax) * (Y1 - Y0);
  for (const s of series) {
    const pts = (s as Series & { _pts: Array<{ d: number; cents: number }> })._pts;
    s.path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${X(p.d).toFixed(1)} ${Y(p.cents).toFixed(1)}`).join(" ");
  }

  series.sort((a, b) => b.ltvCents - a.ltvCents);
  return { series, yMax };
}

/** Cumulative value at day d = the last recorded value at or before d. */
function carried(days: Map<number, number>, d: number): number {
  let best = 0;
  for (const [k, v] of days) if (k <= d && k >= 0) best = Math.max(best, v);
  return best;
}

/**
 * Days every member of a cohort month has had to convert. The last member joined
 * at month end — or today, if the month is still running.
 */
function maturityDays(cohortMonth: string): number {
  const start = new Date(`${cohortMonth.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return 0;
  const endOfMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
  const lastJoin = Math.min(endOfMonth.getTime(), Date.now());
  return Math.max(0, Math.floor((Date.now() - lastJoin) / 86_400_000));
}

function niceCeiling(cents: number): number {
  if (cents <= 0) return 10_000;
  const mag = Math.pow(10, Math.floor(Math.log10(cents)));
  const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  for (const s of steps) if (cents <= s * mag) return s * mag;
  return 10 * mag;
}
